/*
    Who you have written to before.

    Iris Mail could ask its server for a directory of everyone on it. This mail server has no such
    thing — IMAP knows about mailboxes, not people — so the suggestions come from the only source
    that is actually available here: the addresses this browser has sent to and read from.

    That turns out to be most of the value. The addresses somebody types are overwhelmingly ones
    they have written to before, and a list built from real use needs no directory, no permission
    to read one, and no round trip.

    It is per browser, deliberately. Putting it on the server would mean writing a file into
    somebody's mailbox to hold it, and a webmail that quietly creates folders in your mail is not
    a webmail anybody asked for.
*/

const KEY = 'webmail.recents.v1';
const LIMIT = 400;

export interface Suggestion {
    email: string;
    name: string | null;
    /** How many times it has been seen; the tie-break when scores are level. */
    count: number;
    /** When it was last seen, as a timestamp. */
    at: number;
}

function read(): Suggestion[] {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as Suggestion[]) : [];
    } catch {
        return [];
    }
}

function write(list: Suggestion[]): void {
    try {
        localStorage.setItem(KEY, JSON.stringify(list.slice(0, LIMIT)));
    } catch {
        /* a full or disabled store costs autocomplete, nothing more */
    }
}

export function recents(): Suggestion[] {
    return read();
}

/** Records an address that has just been used, so it comes up next time. */
export function remember(entries: { email: string; name?: string | null }[]): void {
    if (!entries.length) return;
    const list = read();
    const byEmail = new Map(list.map((s) => [s.email.toLowerCase(), s]));
    const now = Date.now();

    for (const entry of entries) {
        const email = entry.email.trim();
        if (!email || !email.includes('@')) continue;
        const key = email.toLowerCase();
        const existing = byEmail.get(key);
        if (existing) {
            existing.count += 1;
            existing.at = now;
            // A name learned later is better than no name; a name is not unlearned.
            if (!existing.name && entry.name) existing.name = entry.name;
        } else {
            const fresh: Suggestion = { email, name: entry.name ?? null, count: 1, at: now };
            byEmail.set(key, fresh);
            list.push(fresh);
        }
    }

    // Most used first, most recent as the tie-break, so the list stays useful as it fills up.
    list.sort((a, b) => b.count - a.count || b.at - a.at);
    write(list);
}

/**
 * Matches what somebody has typed so far.
 *
 * Matches anywhere in the address or the name, not just at the start: people search for a company
 * by its domain as often as for a person by their first name.
 */
export function suggest(list: Suggestion[], query: string): Suggestion[] {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const scored = list
        .map((s) => {
            const email = s.email.toLowerCase();
            const name = (s.name ?? '').toLowerCase();
            let score = 0;
            if (email.startsWith(q) || name.startsWith(q)) score = 3;
            else if (name.split(/\s+/).some((word) => word.startsWith(q))) score = 2;
            else if (email.includes(q) || name.includes(q)) score = 1;
            return { s, score };
        })
        .filter((x) => x.score > 0);

    scored.sort((a, b) => b.score - a.score || b.s.count - a.s.count || b.s.at - a.s.at);
    return scored.slice(0, 8).map((x) => x.s);
}
